import axios from 'axios';
import { API_BASE } from '../constants/urls';

/**
 * S3 Uploader utility for direct browser uploads.
 * Handles both single-part (standard) and multi-part uploads with progress.
 */

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks for multipart

/**
 * Get correct MIME type based on file extension
 */
function getContentType(filename) {
    const ext = filename.toLowerCase().split('.').pop();
    const mimeTypes = {
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'zip': 'application/zip',
    };
    return mimeTypes[ext] || 'application/octet-stream';
}

export const uploadToS3 = async (file, articleId, onProgress, prefix = 'dump') => {
    const filename = file.name;
    const fileSize = file.size;

    // Use multipart for files larger than 50MB
    if (fileSize > 50 * 1024 * 1024) {
        return await uploadMultipart(file, articleId, onProgress, prefix);
    } else {
        return await uploadSinglePart(file, articleId, onProgress, prefix);
    }
};

const uploadSinglePart = async (file, articleId, onProgress, prefix) => {
    try {
        // 1. Get presigned URL from backend
        const response = await axios.get(`${API_BASE}/api/storage/presigned-upload-url`, {
            params: { 
                filename: file.name, 
                article_id: articleId, 
                prefix,
                file_type: getContentType(file.name)
            },
            withCredentials: true
        });

        if (!response.data.success) {
            throw new Error(response.data.error || 'Failed to get upload URL');
        }

        const { upload_url, s3_key } = response.data;

        // 2. Upload directly to S3
        await axios.put(upload_url, file, {
            headers: { 'Content-Type': getContentType(file.name) },
            onUploadProgress: (progressEvent) => {
                if (onProgress) {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress(percentCompleted, progressEvent.loaded, progressEvent.total);
                }
            }
        });

        return { success: true, s3_key };
    } catch (error) {
        console.error('Error in single-part S3 upload:', error);
        throw error;
    }
};

const uploadMultipart = async (file, articleId, onProgress, prefix) => {
    try {
        // 1. Initiate multipart upload
        const initResponse = await axios.post(`${API_BASE}/api/storage/multipart/initiate`, {
            filename: file.name,
            article_id: articleId,
            prefix
        }, { withCredentials: true });

        if (!initResponse.data.success) {
            throw new Error(initResponse.data.error || 'Failed to initiate multipart upload');
        }

        const { upload_id, s3_key } = initResponse.data;
        const totalParts = Math.ceil(file.size / CHUNK_SIZE);
        const parts = [];
        let uploadedBytes = 0;

        // 2. Upload parts sequentially (or in small parallel batches if needed)
        for (let i = 1; i <= totalParts; i++) {
            const start = (i - 1) * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);
            const chunk = file.slice(start, end);

            // Get presigned URL for this part
            const partUrlResponse = await axios.get(`${API_BASE}/api/storage/multipart/presigned-part`, {
                params: { s3_key, upload_id, part_number: i },
                withCredentials: true
            });

            if (!partUrlResponse.data.success) {
                throw new Error('Failed to get part upload URL');
            }

            const { url } = partUrlResponse.data;

            // Upload the chunk
            const uploadPartResponse = await axios.put(url, chunk, {
                headers: {
                    'Content-Type': 'application/octet-stream'
                },
                onUploadProgress: (progressEvent) => {
                    const currentChunkProgress = progressEvent.loaded;
                    const totalLoadedSoFar = uploadedBytes + currentChunkProgress;
                    if (onProgress) {
                        const totalPercent = Math.round((totalLoadedSoFar * 100) / file.size);
                        onProgress(totalPercent, totalLoadedSoFar, file.size);
                    }
                }
            });

            const etag = uploadPartResponse.headers.etag;
            parts.push({ ETag: etag, PartNumber: i });
            uploadedBytes += (end - start);
        }

        // 3. Complete multipart upload
        const completeResponse = await axios.post(`${API_BASE}/api/storage/multipart/complete`, {
            s3_key,
            upload_id,
            parts
        }, { withCredentials: true });

        if (!completeResponse.data.success) {
            throw new Error('Failed to complete multipart upload');
        }

        return { success: true, s3_key };
    } catch (error) {
        console.error('Error in multipart S3 upload:', error);
        throw error;
    }
};
